import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { AlertCircle, Loader2, Plus } from "lucide-react";
import { ACCENT, STAGE_COLOR, fmtDate, humanize, Field, HistoryTable } from "./shared";

export default function TnmTab({ data, ref_, onSaved }) {
  const [form, setForm] = useState({
    date: new Date().toISOString().slice(0, 10),
    cancer_type: "breast",
    primary_site: "",
    histology: "",
    grade: "G2",
    t: "T1", n: "N0", m: "M0",
    biomarkers_str: "",
    notes: "",
  });
  const [preview, setPreview] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  async function livePreview(next) {
    try {
      const { data: r } = await api.post("/specialties/oncology/scores/tnm",
        { t: next.t, n: next.n, m: next.m });
      setPreview(r);
    } catch { /* ignore */ }
  }

  useEffect(() => { livePreview(form); /* initial */ }, []);

  function set(key, val) {
    const next = { ...form, [key]: val };
    setForm(next);
    if (["t", "n", "m"].includes(key)) livePreview(next);
  }

  async function submit(e) {
    e.preventDefault();
    setError(""); setSaving(true);
    const biomarkers = {};
    (form.biomarkers_str || "").split(",").forEach((chunk) => {
      const [k, v] = chunk.split(":").map((s) => (s || "").trim());
      if (k) biomarkers[k] = v || "";
    });
    try {
      await api.post("/specialties/oncology/diagnosis", {
        date: form.date, cancer_type: form.cancer_type,
        primary_site: form.primary_site, histology: form.histology || null,
        grade: form.grade || null, t: form.t, n: form.n, m: form.m,
        biomarkers, notes: form.notes || null,
      });
      toast.success("Diagnosis recorded");
      onSaved();
    } catch (err) {
      setError(err?.response?.data?.detail || "Failed to save diagnosis");
    } finally { setSaving(false); }
  }

  const stageColor = preview ? (STAGE_COLOR[preview.stage] || "#A3ACA7") : "#A3ACA7";

  return (
    <div className="space-y-6" data-testid="onco-tnm-tab">
      <form onSubmit={submit} className="ros-card space-y-4" data-testid="tnm-form">
        <div className="flex items-center justify-between">
          <div className="font-heading text-lg">Record diagnosis (AJCC 8th ed.)</div>
          {preview && (
            <div className="rounded-2xl px-4 py-2 text-right"
                 data-testid="tnm-live-preview"
                 style={{ background: `${stageColor}14`, border: `1px solid ${stageColor}55` }}>
              <div className="text-[10px] uppercase tracking-wider text-[#A3ACA7]">Live preview</div>
              <div className="text-2xl font-heading font-bold" style={{ color: stageColor }}>
                Stage {preview.stage} <span className="text-sm text-[#A3ACA7] font-mono">({preview.tnm})</span>
              </div>
              <div className="text-[10px]" style={{ color: stageColor }}>{preview.group_label}</div>
            </div>
          )}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <Field label="Date">
            <input type="date" value={form.date} onChange={(e) => set("date", e.target.value)}
                   data-testid="tnm-input-date" className="ros-input" required />
          </Field>
          <Field label="Cancer type">
            <select value={form.cancer_type} onChange={(e) => set("cancer_type", e.target.value)}
                    data-testid="tnm-input-cancer-type" className="ros-input">
              {ref_.cancer_types.map((c) => <option key={c} value={c}>{humanize(c)}</option>)}
            </select>
          </Field>
          <Field label="Grade">
            <select value={form.grade} onChange={(e) => set("grade", e.target.value)}
                    data-testid="tnm-input-grade" className="ros-input">
              {["G1", "G2", "G3", "G4", "GX"].map((g) => <option key={g} value={g}>{g}</option>)}
            </select>
          </Field>
        </div>

        <div className="grid grid-cols-3 gap-3">
          <Field label="T — Primary tumor">
            <select value={form.t} onChange={(e) => set("t", e.target.value)}
                    data-testid="tnm-input-t" className="ros-input">
              {ref_.t_values.map((v) => <option key={v} value={v}>{v}</option>)}
            </select>
          </Field>
          <Field label="N — Regional nodes">
            <select value={form.n} onChange={(e) => set("n", e.target.value)}
                    data-testid="tnm-input-n" className="ros-input">
              {ref_.n_values.map((v) => <option key={v} value={v}>{v}</option>)}
            </select>
          </Field>
          <Field label="M — Distant metastases">
            <select value={form.m} onChange={(e) => set("m", e.target.value)}
                    data-testid="tnm-input-m" className="ros-input">
              {ref_.m_values.map((v) => <option key={v} value={v}>{v}</option>)}
            </select>
          </Field>
        </div>

        <Field label="Primary site">
          <input type="text" value={form.primary_site}
                 onChange={(e) => set("primary_site", e.target.value)}
                 data-testid="tnm-input-site"
                 placeholder="Left breast, upper outer quadrant" className="ros-input" required />
        </Field>
        <Field label="Histology">
          <input type="text" value={form.histology}
                 onChange={(e) => set("histology", e.target.value)}
                 data-testid="tnm-input-histology"
                 placeholder="Invasive ductal carcinoma" className="ros-input" />
        </Field>
        <Field label="Biomarkers (comma-separated key:value)">
          <input type="text" value={form.biomarkers_str}
                 onChange={(e) => set("biomarkers_str", e.target.value)}
                 data-testid="tnm-input-biomarkers"
                 placeholder="ER:+, PR:+, HER2:+, Ki-67:22%" className="ros-input" />
        </Field>
        <Field label="Notes">
          <textarea rows={2} value={form.notes} onChange={(e) => set("notes", e.target.value)}
                    data-testid="tnm-input-notes"
                    className="ros-input" />
        </Field>

        {preview && (
          <div className="text-xs p-3 rounded-xl border"
               style={{ background: `${stageColor}12`, borderColor: `${stageColor}40` }}
               data-testid="tnm-recommendation">
            <span className="font-semibold text-white">Stage {preview.stage} — {preview.group_label}:</span>{" "}
            {preview.description}
          </div>
        )}

        {error && (
          <div className="text-sm text-[#F87171] flex items-center gap-2" data-testid="tnm-error">
            <AlertCircle size={14} /> {error}
          </div>
        )}

        <div>
          <button type="submit" disabled={saving}
                  data-testid="tnm-submit"
                  className="flex items-center gap-2 px-5 py-2 rounded-full text-black font-semibold disabled:opacity-50 transition-colors"
                  style={{ background: ACCENT }}>
            {saving ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} />}
            {saving ? "Saving…" : "Save diagnosis"}
          </button>
        </div>
      </form>

      <HistoryTable
        title={`Diagnoses (${data.diagnoses.length})`}
        testId="tnm-history"
        rows={data.diagnoses}
        rowTestId={(r) => `tnm-row-${r.id}`}
        empty="No diagnoses recorded."
        columns={[
          { label: "Date",   render: (r) => fmtDate(r.date) },
          { label: "Cancer", render: (r) => <span className="text-xs">{humanize(r.cancer_type)}</span> },
          { label: "TNM",    render: (r) => <span className="font-mono text-xs">{r.tnm}</span> },
          { label: "Stage",  render: (r) => (
            <span className="text-[10px] font-bold px-2 py-0.5 rounded-full"
                  style={{ color: STAGE_COLOR[r.stage], background: `${STAGE_COLOR[r.stage]}18`,
                           border: `1px solid ${STAGE_COLOR[r.stage]}40` }}>
              {r.stage}
            </span>
          )},
          { label: "Site",     render: (r) => <span className="text-xs text-[#A3ACA7]">{r.primary_site}</span> },
          { label: "Biomarkers", render: (r) => (
            <div className="flex flex-wrap gap-1">
              {Object.entries(r.biomarkers || {}).slice(0, 4).map(([k, v]) =>
                <span key={k} className="text-[10px] text-[#A3ACA7] bg-[#1C201E] px-1.5 py-0.5 rounded font-mono">{k}{v}</span>
              )}
            </div>
          )},
        ]}
      />
    </div>
  );
}
